import { Box, Button, Divider, Flex, Text } from '@chakra-ui/react';
import {
  EyeOutline as Eye,
  FilledCircleBadge,
  RecognitionLeaderboardValueEntry,
  resolveRecognitionValueDisplay,
} from '@aslanpay/portal-common-lib';

const RANK_COLORS = [
  'brand.darkPurple',
  'brand.purple',
  'brand.mediumPurple',
  'brand.lightGray1',
  'brand.lightGray1',
];

interface ValuesLeaderboardProps {
  entries: RecognitionLeaderboardValueEntry[];
  hasMore: boolean;
  onSeeAll?: () => void;
}

function EmptyState() {
  return (
    <Flex flexDir="column" align="center" justify="center" py="40px" gap="8px">
      <Text textStyle="h5Medium" color="brand.darkPurple">
        Things are just getting started
      </Text>
      <Text textStyle="small" color="brand.darkGray" textAlign="center">
        Recognition insights will appear here once people begin sending recognitions.
      </Text>
    </Flex>
  );
}

function ValuesLeaderboard({ entries, hasMore, onSeeAll = undefined }: ValuesLeaderboardProps) {
  return (
    <Flex
      bg="brand.lightGray2"
      borderRadius="10px"
      border="1px solid"
      borderColor="brand.lightGray1"
      w="full"
      p="20px 30px"
      flexDir="column"
      gap="20px"
    >
      <Flex justify="space-between" align="flex-start">
        <Flex flexDir="column" gap="4px">
          <Text fontSize="24px" fontWeight="600" lineHeight="1.4" color="brand.mutedPurple">
            Values leaderboard
          </Text>
          <Text textStyle="smallMedium" color="brand.darkGray">
            Most used values (all time)
          </Text>
        </Flex>
        {hasMore && (
          <Button
            variant="ghost"
            size="sm"
            color="brand.purple"
            leftIcon={<Eye boxSize="16px" />}
            _hover={{ bg: 'transparent' }}
            _active={{ bg: 'transparent' }}
            pr={0}
            onClick={onSeeAll}
          >
            See all
          </Button>
        )}
      </Flex>

      {entries.length === 0 ? (
        <EmptyState />
      ) : (
        <Flex flexDir="column">
          {entries.map((entry, index) => (
            <Box key={entry.valueId}>
              {index > 0 && <Divider borderColor="brand.lightGray1" />}
              <Flex justify="space-between" align="center" py="10px" gap="12px">
                <Flex align="center" gap="12px" flex="1" minW="0">
                  <FilledCircleBadge
                    number={index + 1}
                    color={RANK_COLORS[index] ?? 'brand.lightGray1'}
                    textColor={index < 3 ? 'white' : 'brand.darkGray'}
                  />
                  {(() => {
                    const { name, emoji } = resolveRecognitionValueDisplay(
                      entry.valueName,
                      entry.valueEmoji
                    );
                    return (
                      <Text
                        textStyle="smallSemiBold"
                        color="brand.mutedPurple"
                        noOfLines={1}
                        title={`${emoji} ${name}`}
                      >
                        {emoji} {name.trim()}
                      </Text>
                    );
                  })()}
                </Flex>
                <Text textStyle="h5SemiBold" color="brand.mutedPurple" flexShrink={0}>
                  {entry.count.toLocaleString()}
                </Text>
              </Flex>
            </Box>
          ))}
        </Flex>
      )}
    </Flex>
  );
}

export default ValuesLeaderboard;
